// app/javascript/controllers/crud/mask_controller.js
import { Controller } from "@hotwired/stimulus"

function onlyDigits(value) {
  return (value || "").toString().replace(/\D/g, "")
}

// Cursor helpers (evita "pular" em máscaras que inserem caracteres, ex: CEP/Telefone/CPF/Dinheiro/Percent)
function countDigitsBeforeCursor(value, cursorPos) {
  const left = (value || "").toString().slice(0, Math.max(0, cursorPos || 0))
  return (left.match(/\d/g) || []).length
}

function cursorPosFromDigitsCount(maskedValue, digitsCount) {
  if (digitsCount <= 0) return 0

  let count = 0
  const s = (maskedValue || "").toString()

  for (let i = 0; i < s.length; i++) {
    if (/\d/.test(s[i])) count++
    if (count >= digitsCount) return i + 1 // posição após o dígito
  }

  return s.length
}

// 12345678 -> 12345-678
function formatCepBR(value) {
  const d = onlyDigits(value).slice(0, 8)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

// 12345678901 -> 123.456.789-01
function formatCpfBR(value) {
  const d = onlyDigits(value).slice(0, 11)

  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

// 123456 -> 1.234,56
function formatMoneyNumberBR(value) {
  const digits = onlyDigits(value)
  if (!digits) return "0,00"

  const number = parseInt(digits, 10)
  const cents = number / 100

  return cents.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatMoneyBR(value, withPrefix) {
  const n = formatMoneyNumberBR(value)
  return withPrefix ? `R$ ${n}` : n
}

// Percent:
// - aceita "0,40" -> 40%
// - aceita "40" -> 40%
// - aceita "40%" -> 40%
function formatPercent(value, { live = false } = {}) {
  const raw = (value || "").toString().trim()
  if (!raw) return ""

  // deixa apenas dígitos, ponto e vírgula (remove % e outras coisas)
  const cleaned = raw.replace(/[^\d,.-]/g, "").replace(",", ".")
  let num = parseFloat(cleaned)

  if (isNaN(num)) return live ? raw.replace(/[^\d]/g, "") : ""

  // se for 0.x assume fração
  if (num <= 1) num = num * 100

  // no live a gente não fica "pulando" números demais
  const rounded = Math.round(num)
  return `${rounded}%`
}

// (11) 91234-5678 | (11) 1234-5678
function formatPhoneBR(value) {
  const d = onlyDigits(value).slice(0, 11)

  if (d.length <= 2) return d
  const ddd = d.slice(0, 2)
  const rest = d.slice(2)

  // celular (9 dígitos)
  if (rest.length >= 9) {
    const p1 = rest.slice(0, 5)
    const p2 = rest.slice(5, 9)
    return `(${ddd}) ${p1}-${p2}`
  }

  // fixo (8 dígitos)
  if (rest.length >= 8) {
    const p1 = rest.slice(0, 4)
    const p2 = rest.slice(4, 8)
    return `(${ddd}) ${p1}-${p2}`
  }

  return `(${ddd}) ${rest}`
}

export default class extends Controller {
  static values = {
    kind: String, // money | percent | phone | cep | cpf
    prefix: { type: Boolean, default: false }, // money prefix "R$"
  }

  connect() {
    this.formatInitial()
  }

  formatInitial() {
    if (!this.hasKindValue) return

    // ✅ MONEY: se usuário está digitando no fim, mantém no fim (evita pulo/zeros extras)
    if (this.kindValue === "money") {
      const cursor = this.element.selectionStart
      const wasAtEnd = cursor === this.element.value.length
      const digitsBefore = countDigitsBeforeCursor(this.element.value, cursor)

      const masked = formatMoneyBR(this.element.value, this.prefixValue)
      this.element.value = masked

      if (wasAtEnd) {
        this.setCursor(masked.length)
      } else {
        const newPos = cursorPosFromDigitsCount(masked, digitsBefore)
        this.setCursor(newPos)
      }
      return
    }

    if (this.kindValue === "percent") {
      this.element.value = formatPercent(this.element.value, { live: false })
    }

    if (this.kindValue === "phone") {
      this.element.value = formatPhoneBR(this.element.value)
    }

    if (this.kindValue === "cep") {
      this.element.value = formatCepBR(this.element.value)
    }

    if (this.kindValue === "cpf") {
      this.element.value = formatCpfBR(this.element.value)
    }
  }

  onInput() {
    if (!this.hasKindValue) return

    // ✅ MONEY: preserva cursor por nº de dígitos antes do cursor
    if (this.kindValue === "money") {
      const cursor = this.element.selectionStart
      const digitsBefore = countDigitsBeforeCursor(this.element.value, cursor)

      const masked = formatMoneyBR(this.element.value, this.prefixValue)
      this.element.value = masked

      const newPos = cursorPosFromDigitsCount(masked, digitsBefore)
      this.setCursor(newPos)
      return
    }

    // ✅ PERCENT: também preserva cursor (por nº de dígitos) ao inserir '%'
    if (this.kindValue === "percent") {
      const cursor = this.element.selectionStart
      const digitsBefore = countDigitsBeforeCursor(this.element.value, cursor)

      const masked = formatPercent(this.element.value, { live: true })
      this.element.value = masked

      const newPos = cursorPosFromDigitsCount(masked, digitsBefore)
      this.setCursor(newPos)
      return
    }

    // ✅ PHONE/CEP/CPF: preserva cursor por "nº de dígitos antes"
    if (this.kindValue === "phone") {
      const cursor = this.element.selectionStart
      const digitsBefore = countDigitsBeforeCursor(this.element.value, cursor)

      const masked = formatPhoneBR(this.element.value)
      this.element.value = masked

      const newPos = cursorPosFromDigitsCount(masked, digitsBefore)
      this.setCursor(newPos)
      return
    }

    if (this.kindValue === "cep") {
      const cursor = this.element.selectionStart
      const digitsBefore = countDigitsBeforeCursor(this.element.value, cursor)

      const masked = formatCepBR(this.element.value)
      this.element.value = masked

      const newPos = cursorPosFromDigitsCount(masked, digitsBefore)
      this.setCursor(newPos)
      return
    }

    if (this.kindValue === "cpf") {
      const cursor = this.element.selectionStart
      const digitsBefore = countDigitsBeforeCursor(this.element.value, cursor)

      const masked = formatCpfBR(this.element.value)
      this.element.value = masked

      const newPos = cursorPosFromDigitsCount(masked, digitsBefore)
      this.setCursor(newPos)
      return
    }
  }

  onBlur() {
    if (!this.hasKindValue) return

    if (this.kindValue === "money") {
      this.element.value = formatMoneyBR(this.element.value, this.prefixValue)
    }

    if (this.kindValue === "percent") {
      this.element.value = formatPercent(this.element.value, { live: false })
    }

    if (this.kindValue === "phone") {
      this.element.value = formatPhoneBR(this.element.value)
    }

    if (this.kindValue === "cep") {
      this.element.value = formatCepBR(this.element.value)
    }

    if (this.kindValue === "cpf") {
      this.element.value = formatCpfBR(this.element.value)
    }
  }

  setCursor(pos) {
    try {
      this.element.setSelectionRange(pos, pos)
    } catch (_e) { }
  }
}
